/**
 * Endpoints du domaine ledger (comptabilité) — source canonique.
 * Chemins copiés TELS QUELS de la collection Postman / du guide §6.4
 * (préfixe gateway `/gw/ledger`). Ne jamais inventer un chemin.
 * `@/core/api/endpoints` ré-exporte ces valeurs : ajouter les routes ICI.
 */
export const ledgerEndpoints = {
  journalEntries: {
    /**
     * POST — flashpay-service, flashpay-superadmin, flashpay-admin.
     * Étape 2 du flux transaction : comptabilise l'opération via le modèle
     * d'imputation actif. Idempotency-Key OBLIGATOIRE (guide §4.7).
     */
    post: "/gw/ledger/api/v1/JournalEntries/post",
    /** POST — liste paginée, corps `ListRequest<JournalEntryFilter>`. */
    all: "/gw/ledger/api/v1/JournalEntries/all",
    /** GET — détail d'une écriture et de ses lignes débit/crédit. */
    detail: (journalEntryId: string) =>
      `/gw/ledger/api/v1/JournalEntries/${journalEntryId}`,
    /**
     * POST — flashpay-superadmin, flashpay-admin. Contre-passation
     * manuelle. Idempotency-Key OBLIGATOIRE + `approvalRequestId` de type
     * LEDGER_MANUAL_REVERSAL au statut APPROVED (idempotence serveur sur
     * l'identifiant d'approbation) — TODO phase 8 : rerouter via
     * ApprovalRequests (maker-checker).
     */
    reverse: (journalEntryId: string) =>
      `/gw/ledger/api/v1/JournalEntries/${journalEntryId}/reverse`,
  },
  chartOfAccounts: {
    /** POST — flashpay-superadmin, flashpay-admin. Crée un compte. */
    add: "/gw/ledger/api/v1/ChartOfAccounts/add",
    /** POST — plan de comptes paginé, corps `ListRequest<ChartOfAccountFilter>`. */
    all: "/gw/ledger/api/v1/ChartOfAccounts/all",
    /** GET — détail d'un compte et de son compte parent. */
    detail: (chartOfAccountId: string) =>
      `/gw/ledger/api/v1/ChartOfAccounts/${chartOfAccountId}`,
    /** PUT — flashpay-superadmin, flashpay-admin. Met à jour un compte. */
    update: (chartOfAccountId: string) =>
      `/gw/ledger/api/v1/ChartOfAccounts/${chartOfAccountId}`,
    /** DELETE — flashpay-superadmin. Désactive un compte (pas de suppression physique). */
    delete: (chartOfAccountId: string) =>
      `/gw/ledger/api/v1/ChartOfAccounts/${chartOfAccountId}`,
  },
  postingTemplates: {
    /** POST — flashpay-superadmin, flashpay-admin. Crée un modèle en DRAFT. */
    add: "/gw/ledger/api/v1/PostingTemplates/add",
    /** POST — liste paginée, corps `ListRequest<PostingTemplateFilter>`. */
    all: "/gw/ledger/api/v1/PostingTemplates/all",
    /** GET — détail d'un modèle et de ses lignes. */
    detail: (postingTemplateId: string) =>
      `/gw/ledger/api/v1/PostingTemplates/${postingTemplateId}`,
    /**
     * POST — flashpay-superadmin, flashpay-admin. Active un modèle (un
     * seul actif par couple code produit / provider : le précédent est
     * archivé).
     */
    activate: (postingTemplateId: string) =>
      `/gw/ledger/api/v1/PostingTemplates/${postingTemplateId}/activate`,
    /** POST — flashpay-superadmin, flashpay-admin. Archive un modèle. */
    archive: (postingTemplateId: string) =>
      `/gw/ledger/api/v1/PostingTemplates/${postingTemplateId}/archive`,
  },
  productCodes: {
    /** POST — flashpay-superadmin, flashpay-admin. Crée un code produit. */
    add: "/gw/ledger/api/v1/ProductCodes/add",
    /** POST — liste paginée, corps `ListRequest<ProductCodeFilter>`. */
    all: "/gw/ledger/api/v1/ProductCodes/all",
    /** GET — détail d'un code produit. */
    detail: (productCodeId: string) =>
      `/gw/ledger/api/v1/ProductCodes/${productCodeId}`,
    /** GET — recherche d'un code produit par son code logique. */
    byCode: (code: string) => `/gw/ledger/api/v1/ProductCodes/code/${code}`,
    /** PUT — flashpay-superadmin, flashpay-admin. Met à jour un code produit. */
    update: (productCodeId: string) =>
      `/gw/ledger/api/v1/ProductCodes/${productCodeId}`,
    /** DELETE — flashpay-superadmin. Désactive un code produit. */
    delete: (productCodeId: string) =>
      `/gw/ledger/api/v1/ProductCodes/${productCodeId}`,
  },
  providers: {
    /** POST — flashpay-superadmin, flashpay-admin. Enregistre un provider. */
    add: "/gw/ledger/api/v1/Providers/add",
    /** POST — liste paginée, corps `ListRequest<ProviderFilter>`. */
    all: "/gw/ledger/api/v1/Providers/all",
    /** GET — détail d'un provider. */
    detail: (providerId: string) =>
      `/gw/ledger/api/v1/Providers/${providerId}`,
    /** PUT — flashpay-superadmin, flashpay-admin. Met à jour un provider. */
    update: (providerId: string) =>
      `/gw/ledger/api/v1/Providers/${providerId}`,
    /** DELETE — flashpay-superadmin. Désactive un provider. */
    delete: (providerId: string) =>
      `/gw/ledger/api/v1/Providers/${providerId}`,
  },
  productProviderMappings: {
    /**
     * POST — flashpay-superadmin, flashpay-admin. Associe un code produit
     * à un provider avec sa configuration d'intégration.
     */
    add: "/gw/ledger/api/v1/ProductProviderMappings/add",
    /** POST — liste paginée, corps `ListRequest<ProductProviderMappingFilter>`. */
    all: "/gw/ledger/api/v1/ProductProviderMappings/all",
    /** GET — détail d'une association et de sa configuration externe. */
    detail: (productProviderMappingId: string) =>
      `/gw/ledger/api/v1/ProductProviderMappings/${productProviderMappingId}`,
    /** DELETE — flashpay-superadmin. Désactive une association. */
    delete: (productProviderMappingId: string) =>
      `/gw/ledger/api/v1/ProductProviderMappings/${productProviderMappingId}`,
  },
  fxRates: {
    /** POST — flashpay-superadmin, flashpay-admin. Enregistre un taux horodaté. */
    add: "/gw/ledger/api/v1/FxRates/add",
    /** POST — historique paginé par couple de devises, corps `ListRequest<FxRateFilter>`. */
    all: "/gw/ledger/api/v1/FxRates/all",
  },
} as const;
